import { router } from '../utils/router.util';
import { authService } from '../services/auth.service';
import { artemisService } from '../services/artemis.service';
import { ROUTES } from '../constants/routes.constants';
import { STORAGE_KEYS } from '../constants/storage.constants';
import { logger } from '../utils/logger.util';

export class SplashPage {
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    logger.debug('[SplashPage] render() chamado');
    
    this.container.innerHTML = `
      <div class="splash-screen">
        <div class="splash-content">
          <img src="/vivo-ads-logo.svg" alt="Vivo Ads" class="splash-logo" />
          <div class="splash-spinner"></div>
        </div>
      </div>
    `;

    logger.debug('[SplashPage] Iniciando inicialização da SDK Artemis...');
    
    // Inicializar o Artemis - a instância será armazenada globalmente
    try {
      await artemisService.initialize();
      logger.debug('[SplashPage] SDK Artemis inicializada com sucesso');
    } catch (error) {
      logger.error('[SplashPage] Erro ao inicializar SDK Artemis:', error);
      // Mesmo com erro, continuar - o app pode funcionar parcialmente
    }
    
    // Após inicialização, navegar
    await this.navigateToNextPage();
  }

  private async navigateToNextPage(): Promise<void> {
    logger.debug('[SplashPage] navigateToNextPage() chamado - Progresso completo');
    
    // Verificar se há uma rota pendente salva
    const pendingRoute = sessionStorage.getItem(STORAGE_KEYS.PENDING_ROUTE);
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_ROUTE);
    
    logger.debug('[SplashPage] Rota pendente:', pendingRoute);
    
    // Se o usuário possui token de sessão, validar antes de navegar
    if (authService.isAuthenticated()) {
      logger.debug('[SplashPage] Token encontrado, validando sessão...');
      try {
        const isValid = await authService.validateSession();
        if (!isValid) {
          logger.debug('[SplashPage] Sessão expirada, tentando Header Enrichment...');
          // Limpar storage e tentar HE
          await authService.logout();
          await this.tryHeaderEnrichment(pendingRoute);
          return;
        }
        logger.debug('[SplashPage] Sessão válida, continuando navegação');
        this.performNavigationWithRoute(pendingRoute);
        return;
      } catch (error) {
        logger.error('[SplashPage] Erro ao validar sessão:', error);
        // Em caso de erro, assumir que a sessão expirou e tentar HE
        await authService.logout();
        await this.tryHeaderEnrichment(pendingRoute);
        return;
      }
    }
    
    // Se não tem token, tentar Header Enrichment
    logger.debug('[SplashPage] Nenhum token encontrado, tentando Header Enrichment...');
    await this.tryHeaderEnrichment(pendingRoute);
  }

  /**
   * Tenta fazer login via Header Enrichment
   * Se falhar, redireciona para login
   */
  private async tryHeaderEnrichment(pendingRoute: string | null): Promise<void> {
    try {
      const result = await authService.loginWithHeaderEnrichment();
      if (result) {
        logger.debug('[SplashPage] Header Enrichment bem-sucedido, redirecionando para home');
        // Login bem-sucedido com HE, redirecionar para home
        this.performNavigationWithRoute(pendingRoute || '/home');
        return;
      }
    } catch (error) {
      // Falha silenciosa, continuar para login
      logger.warnWithContext('SplashPage', 'Header Enrichment falhou, redirecionando para login:', error);
    }
    
    // Se chegou aqui, HE falhou, redirecionar para login
    logger.debug('[SplashPage] Header Enrichment não disponível, redirecionando para login');
    this.performNavigation('/login');
  }

  /**
   * Realiza navegação considerando rota pendente ou estado de autenticação
   */
  private performNavigationWithRoute(pendingRoute: string | null): void {
    const splashScreen = this.container.querySelector('.splash-screen');
    if (splashScreen) {
      logger.debug('[SplashPage] Adicionando classe de fade out');
      splashScreen.classList.add('splash-screen-hide');
      setTimeout(() => {
        if (pendingRoute && pendingRoute !== '/splash') {
          // Navegar para a rota que o usuário tentou acessar originalmente
          logger.debug('[SplashPage] Navegando para rota pendente:', pendingRoute);
          router.navigate(pendingRoute);
        } else {
          // Navegar para a próxima página baseado no estado de autenticação
          if (authService.isAuthenticated()) {
            logger.debug('[SplashPage] Navegando para /home (usuário autenticado)');
            router.navigate(ROUTES.HOME);
          } else {
            logger.debug('[SplashPage] Navegando para /login (usuário não autenticado)');
            router.navigate(ROUTES.LOGIN);
          }
        }
      }, 300); // Aguardar animação de fade out
    } else {
      logger.warnWithContext('SplashPage', 'Elemento splash-screen não encontrado!');
      // Mesmo sem elemento, fazer navegação
      if (pendingRoute && pendingRoute !== '/splash') {
        router.navigate(pendingRoute);
      } else {
        router.navigate(authService.isAuthenticated() ? ROUTES.HOME : ROUTES.LOGIN);
      }
    }
  }

  private performNavigation(route: string): void {
    const splashScreen = this.container.querySelector('.splash-screen');
    if (splashScreen) {
      splashScreen.classList.add('splash-screen-hide');
      setTimeout(() => {
        router.navigate(route);
      }, 300);
    } else {
      router.navigate(route);
    }
  }
}

